import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { toast, Toaster } from "react-hot-toast";

import { AvatarMedia, Loader, useWindowSize, Wrapper } from "../common";

import { HomeURL } from "../../Constants";
import { formatI18nKeys, toI18N } from "../../i18n";
import fetchApi from "../../services/FetchApi";
import * as GoogleAnalyticsService from "../../services/GoogleAnalyticsService";
import { establishSession } from "../../services/SessionApi";
import { isDev } from "../../utils/DebugUtils";
import { authErrorMessage } from "../../utils/authErrors";
import { emitLoginSuccess, isUserFromChina } from "../../utils/helpers";
import { decodeOAuthState } from "../../utils/oauthState";
import { isValidMobileRedirectUri, isValidRedirectUrl } from "../../utils/redirect";
import { validateEmail } from "../../utils/Validator";
import BirthdayVerification from "./BirthdayVerification";
import EmailScreen from "./EmailScreen";
import PhoneVerification from "./PhoneVerification";

// Get target origin for postMessage from HomeURL
const getTargetOrigin = (): string => {
	try {
		if (HomeURL) {
			const url = new URL(HomeURL);
			return url.origin;
		}
	} catch {
		// Fall through
	}
	return window.location.origin;
};

// Keys used for OAuth flow that should be cleared after auth
const AUTH_STORAGE_KEYS = [
	"provider",
	"type",
	"path",
	"link-id",
	"select-id",
	"group-id",
	"disableApple",
	"useCodeVerification",
	"temp-user-id",
	"redirectUri",
	"branding",
];

// Analytics only. The community arrives in the query string on the first tab
// and nowhere else, so on the OAuth return and on the tab a magic link opens
// every event sent an empty community. Kept the way link-id already is:
// written when seen, restored from storage when the query lacks it.
const readStoredOrNull = (key: string): string | null => {
	const value = localStorage.getItem(key);
	return value && value !== "null" ? value : null;
};

// Community branding lives only in the URL query string, which OAuth returns
// and email-link tabs arrive without. Stash it when a flow starts and restore
// it only on entries that carry a continuation marker (code/challengeToken),
// so direct visits never inherit a previous community's branding.
const BRANDING_STORAGE_KEY = "branding";
const BRANDING_TTL_MS = 30 * 60 * 1000;

type StashedBranding = {
	logo?: string;
	title?: string;
	coverImage?: string;
	backgroundColor?: string;
	ts: number;
};

const stashBranding = (branding: Omit<StashedBranding, "ts">) => {
	if (
		!branding.logo &&
		!branding.title &&
		!branding.coverImage &&
		!branding.backgroundColor
	) {
		// An unbranded flow must not resurrect an older community's stash.
		localStorage.removeItem(BRANDING_STORAGE_KEY);
		return;
	}
	localStorage.setItem(
		BRANDING_STORAGE_KEY,
		JSON.stringify({ ...branding, ts: Date.now() }),
	);
};

const readStashedBranding = (): StashedBranding | null => {
	try {
		const raw = localStorage.getItem(BRANDING_STORAGE_KEY);
		if (!raw) return null;
		const data = JSON.parse(raw);
		if (!data?.ts || Date.now() - data.ts > BRANDING_TTL_MS) return null;
		return data;
	} catch {
		return null;
	}
};

export type QueryParams = {
	backgroundColor: string;
	groupId: string;
	fontColor: string;
	logo: string;
	title: string;
	linkId: string;
	path: string;
	selectId: string;
	userId?: string;
	description: string;
	type?: string;
	useCodeVerification?: boolean;
	postSlug?: string;
	challengeToken?: string;
	code?: string;
	coverImage?: string;
	disableApple: boolean;
	inChina?: boolean;
	redirectUri?: string;
	state?: string;
	journey_id?: string;
};

export type SignupProps = {
	challengeId: string;
	// Set when login is a step of a "Sign in with Select" authorization.
	// The OAuth flow takes over once the session cookie is saved.
	oauthRequest?: string;
	onAuthenticated?: () => void;
	linkId?: string;
	title?: string;
	logo?: string;
	coverImage?: string;
	backgroundColor?: string;
};

const getQueryData = (query: any): QueryParams => ({
	groupId: query.get("groupId") as string,
	fontColor: query.get("fontColor") as string,
	logo: query.get("logo") as string,
	title: query.get("title") as string,
	linkId: query.get("linkId") as string,
	backgroundColor: query.get("backgroundColor") as string,
	path: query.get("path") as string,
	selectId: query.get("selectId") as string,
	userId: query.get("userId") as string,
	description: query.get("description") as string,
	type: query.get("type") as string,
	useCodeVerification: query.get("useCodeVerification") as boolean,
	postSlug: query.get("postSlug") as string,
	challengeToken: query.get("challengeToken") as string,
	code: query.get("code") as string,
	coverImage: (query.get("coverImage") as string) || "",
	disableApple: query.get("disableApple") as boolean,
	inChina: !!query.get("inChina") as boolean,
	redirectUri: query.get("redirectUri") as string,
	state: query.get("state") as string,
	journey_id: query.get("journey_id") as string,
});

function Signup(props: SignupProps) {
	const query = new URLSearchParams(window.location.search);
	const params = getQueryData(query);

	const stateData = params.state ? decodeOAuthState(params.state) : null;

	// Identity-link relay: a provider-connect flow started on another product
	// page (e.g. the web client's /settings X link) uses this origin ONLY as
	// its registered OAuth callback. Forward the provider's response (code or
	// error, plus the untouched state for the caller's CSRF check) to the
	// requesting page without logging anyone in. `mode` is deliberately a
	// separate field from `type`, which steers post-login behavior.
	const isLinkRelay =
		stateData != null &&
		stateData.mode === "identity-link" &&
		!!stateData.redirectUri &&
		isValidRedirectUrl(stateData.redirectUri);

	if (isLinkRelay) {
		const target = new URL(stateData.redirectUri, window.location.origin);
		if (params.code) target.searchParams.set("code", params.code);
		if (params.state) target.searchParams.set("state", params.state);
		const oauthError = query.get("error");
		if (oauthError) target.searchParams.set("error", oauthError);
		window.location.replace(target.toString());
	}

	// Re-populate localStorage from OAuth state parameter (synchronous, before useMemo)
	// ASWebAuthenticationSession may not persist localStorage across domain navigations,
	// so we encode auth state in Google/Apple's state param and restore it here.
	// Skipped while relaying — link-flow values must not pollute auth storage.
	if (!isLinkRelay && params.code && params.state && stateData) {
		if (stateData.provider) localStorage.setItem("provider", stateData.provider);
		if (stateData.type) localStorage.setItem("type", stateData.type);
		if (stateData.redirectUri) localStorage.setItem("redirectUri", stateData.redirectUri);
		if (stateData.linkId) localStorage.setItem("link-id", stateData.linkId);
		if (stateData.selectId) localStorage.setItem("select-id", stateData.selectId);
		if (stateData.groupId) localStorage.setItem("group-id", stateData.groupId);
	}

	// Analytics only: remember the community from the first tab so the
	// continuation legs (OAuth return, magic-link tab) can report it.
	if (!isLinkRelay) {
		if (params.selectId) localStorage.setItem("select-id", params.selectId);
		if (params.groupId) localStorage.setItem("group-id", params.groupId);
	}

	const [isCodeVerificationScreen, setIsCodeVerificationScreen] =
		useState<boolean>(false);
	const [codeError, setCodeError] = useState<string>("");
	const [successSignIn, setSuccessSignIn] = useState<boolean>(false);
	const [debugEmailToken, setDebugEmailToken] = useState<string>();
	const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
	const [email, setEmail] = useState<string>("");
	const [jwtToken, setJwtToken] = useState<string>("");
	const [emailSuccess, setEmailSuccess] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const [verifyLockSeconds, setVerifyLockSeconds] = useState(0);
	const [verifyLockNonce, setVerifyLockNonce] = useState(0);
	const [inIframe, setInIframe] = useState(false);
	const [phoneVerificationRequired, setPhoneVerificationRequired] =
		useState<boolean>(false);
	const [userIdForPhoneVerification, setUserIdForPhoneVerification] =
		useState<string>("");
	const [isCheckingLocation, setIsCheckingLocation] = useState<boolean>(false);
	const [birthdayVerificationRequired, setBirthdayVerificationRequired] =
		useState<boolean>(false);
	const [userIdForBirthdayVerification, setUserIdForBirthdayVerification] =
		useState<string>("");
	const [
		refreshTokenForBirthdayVerification,
		setRefreshTokenForBirthdayVerification,
	] = useState<string>("");
	const [
		refreshTokenV2ForBirthdayVerification,
		setRefreshTokenV2ForBirthdayVerification,
	] = useState<string | undefined>(undefined);
	const [refreshTokenExpiresAt, setRefreshTokenExpiresAt] = useState<
		string | undefined
	>(undefined);
	const [
		phoneNumberForBirthdayVerification,
		setPhoneNumberForBirthdayVerification,
	] = useState<string>("");
	// Pending signup (compliance): identity verified but no account exists yet.
	// The backend hands back a signed signup token instead of a user, and only
	// creates the account at /complete-signup once the age check passes.
	const [signupToken, setSignupToken] = useState<string>("");
	const [signupMethod, setSignupMethod] = useState<string>("");
	const size = useWindowSize();

	// Define functions that are used in dependency arrays of other hooks
	const validateAge = useCallback((dateString: string): boolean => {
		if (!dateString) return false;
		const birthDate = new Date(dateString);
		const today = new Date();
		let age = today.getFullYear() - birthDate.getFullYear();
		const monthDiff = today.getMonth() - birthDate.getMonth();

		// Adjust age if birthday hasn't occurred this year yet
		if (
			monthDiff < 0 ||
			(monthDiff === 0 && today.getDate() < birthDate.getDate())
		) {
			age--;
		}

		return age >= 16;
	}, []);

	// Create a ref to store the handleBirthdayVerified function
	const handleBirthdayVerifiedRef =
		useRef<(userId: string, refreshToken: string, refreshTokenV2?: string, expiresAtOverride?: string) => Promise<void>>(undefined);

	const handleCheckUserLocationAndRequirePhoneVerification = useCallback(
		async (
			userId: string,
			refreshToken: string,
			refreshTokenV2?: string,
			phoneNumber?: string,
			birthday?: string,
			expiresAt?: string,
		) => {
			setIsCheckingLocation(true);
			setRefreshTokenExpiresAt(expiresAt);
			try {
				const isChina = await isUserFromChina();

				if ((isChina && !phoneNumber) || (params.inChina && !phoneNumber)) {
					// User is from China and doesn't have a phone number, require phone verification
					// This maintains the unified flow where all signup methods follow the same path
					setUserIdForPhoneVerification(userId);
					setPhoneVerificationRequired(true);
				} else {
					// User is not from China or already has a phone number, proceed to birthday verification
					// Check if user already has birthday set and is over 16
					if (birthday && validateAge(birthday)) {
						// User already has birthday and is over 16, skip birthday verification
						if (handleBirthdayVerifiedRef.current) {
							await handleBirthdayVerifiedRef.current(userId, refreshToken, refreshTokenV2);
						}
						return;
					}

					// This maintains the unified flow where all signup methods require birthday verification as the final step
					setUserIdForBirthdayVerification(userId);
					setRefreshTokenForBirthdayVerification(refreshToken);
					setRefreshTokenV2ForBirthdayVerification(refreshTokenV2);
					setPhoneNumberForBirthdayVerification(phoneNumber || "");
					setBirthdayVerificationRequired(true);
				}
			} catch (error) {
				isDev() && console.error("Error checking user location:", error);
				// In case of error, proceed to birthday verification to ensure age requirement is met
				// Check if user already has birthday set and is over 16
				if (birthday && validateAge(birthday)) {
					// User already has birthday and is over 16, skip birthday verification
					if (handleBirthdayVerifiedRef.current) {
						await handleBirthdayVerifiedRef.current(userId, refreshToken, refreshTokenV2);
					}
					return;
				}

				// This maintains the unified flow where all signup methods require birthday verification as the final step
				setUserIdForBirthdayVerification(userId);
				setRefreshTokenForBirthdayVerification(refreshToken);
				setRefreshTokenV2ForBirthdayVerification(refreshTokenV2);
				setPhoneNumberForBirthdayVerification(phoneNumber || "");
				setBirthdayVerificationRequired(true);
			} finally {
				setIsCheckingLocation(false);
			}
		},
		[
			setIsCheckingLocation,
			setUserIdForPhoneVerification,
			setPhoneVerificationRequired,
			setUserIdForBirthdayVerification,
			setRefreshTokenForBirthdayVerification,
			setPhoneNumberForBirthdayVerification,
			setBirthdayVerificationRequired,
			params.inChina,
			validateAge,
		],
	);

	// Every path that resolves the flow here sets this and ends the wait. The
	// poll's own pending branch deliberately does not: that is still this tab
	// waiting.
	const [challengeResolvedHere, setChallengeResolvedHere] = useState(false);
	// State cannot stop a tick that is already in flight, because it holds its
	// own closure. The ref is what that tick can read back after its request.
	const resolvedHereRef = useRef(false);
	const markResolvedHere = useCallback(() => {
		resolvedHereRef.current = true;
		setChallengeResolvedHere(true);
	}, []);
	const releaseResolvedHere = useCallback(() => {
		resolvedHereRef.current = false;
		setChallengeResolvedHere(false);
	}, []);

	const handleSignInSuccess = useCallback(
		async (
			userId: string,
			refreshToken: string,
			refreshTokenV2?: string,
			phoneNumber?: string,
			birthday?: string,
			expiresAt?: string,
		) => {
			if (!userId || !refreshToken) {
				toast.error(toI18N("lp_login_otp_error_generic"));
				return;
			}
			markResolvedHere();
			// Existing account: a signup token left over from an abandoned pending
			// signup must not hijack the birthday screen into creating a new user
			setSignupToken("");
			await handleCheckUserLocationAndRequirePhoneVerification(
				userId,
				refreshToken,
				refreshTokenV2,
				phoneNumber,
				birthday,
				expiresAt,
			);
		},
		[handleCheckUserLocationAndRequirePhoneVerification, markResolvedHere],
	);

	// Entry point for new users: no account yet, so route them through the same
	// phone/birthday screens carrying only the signup token.
	const handlePendingSignup = useCallback(
		async (token: string, method: string) => {
			setSignupToken(token);
			setSignupMethod(method);
			setIsCheckingLocation(true);
			try {
				const isChina = await isUserFromChina();
				if (isChina || params.inChina) {
					// A brand-new signup never has a phone number yet
					setPhoneVerificationRequired(true);
				} else {
					setBirthdayVerificationRequired(true);
				}
			} catch (error) {
				isDev() && console.error("Error checking user location:", error);
				// Age verification must still happen, so fall through to the birthday screen
				setBirthdayVerificationRequired(true);
			} finally {
				setIsCheckingLocation(false);
			}
		},
		[params.inChina],
	);

	const handlePendingPhoneVerified = useCallback((newSignupToken: string) => {
		// Phone folded into a fresh signup token; the age screen is next
		setSignupToken(newSignupToken);
		setBirthdayVerificationRequired(true);
	}, []);

	const {
		groupId,
		useCodeVerification,
		postSlug,
		disableApple,
	} = params;

	// Analytics only. `selectId` (the community slug) is read by nothing but
	// the events in this file, so it is restored from storage in place.
	// `groupId` (the community UUID) also steers product logic below, so the
	// restored value lives in its own analytics-only name and `groupId` stays
	// exactly what the query string said.
	// Storage is consulted only on a continuation of a flow (the OAuth return
	// carries `code`, the magic-link tab carries `challengeToken`), the same
	// rule the branding stash uses: a direct visit with no community in the
	// query must not inherit one from an earlier, abandoned flow.
	const isFlowContinuation = !!(params.code || params.challengeToken);
	const selectId = useMemo(
		() =>
			params.selectId ||
			(isFlowContinuation ? readStoredOrNull("select-id") : null),
		[params, isFlowContinuation],
	);
	const groupIdForAnalytics = useMemo(
		() =>
			params.groupId ||
			(isFlowContinuation ? readStoredOrNull("group-id") : null),
		[params, isFlowContinuation],
	);

	const stashedBranding = useMemo(() => {
		const hasUrlBranding = !!(
			params.logo ||
			params.title ||
			params.coverImage ||
			params.backgroundColor
		);
		const isFlowContinuation = !!(params.code || params.challengeToken);
		if (hasUrlBranding || !isFlowContinuation) return null;
		return readStashedBranding();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const logo = params.logo || props.logo || stashedBranding?.logo || "";
	const title = params.title || props.title || stashedBranding?.title || "";
	const coverImage =
		params.coverImage || props.coverImage || stashedBranding?.coverImage || "";
	const backgroundColor =
		params.backgroundColor ||
		props.backgroundColor ||
		stashedBranding?.backgroundColor ||
		"";

	const type = useMemo(() => {
		const localType = localStorage.getItem("type");
		if (localType && localType !== "null") {
			return localType;
		}
		if (params && params.type) {
			return params.type;
		}
		return null;
	}, [params]);

	// Analytics only, synchronous in render on purpose: the OAuth-return and
	// magic-link effects below emit on mount, so the origin has to be set
	// before any effect runs. Idempotent; `type` already survives the OAuth
	// round-trip through localStorage and the state param.
	GoogleAnalyticsService.setOrigin(type);

	const path = useMemo(() => {
		const localType = localStorage.getItem("path");
		if (localType && localType !== "null") {
			return localType;
		}
		if (params && params.path) {
			return params.path;
		}
		return null;
	}, [params]);

	const linkId = useMemo(() => {
		if (props.linkId) {
			return props.linkId;
		}
		if (params && params.linkId) {
			return params.linkId;
		}
		const localType = localStorage.getItem("link-id");
		if (localType && localType !== "null") {
			return localType;
		}
		return null;
	}, [params, props.linkId]);

	// On the OAuth return leg the URL has only `state`, so the id rides inside it.
	const journeyId = params?.journey_id || stateData?.journeyId || null;

	// One shape for every sign-in row, so the id cannot be added to some and
	// missed on others.
	const signInIds = useMemo(
		() => ({
			group_id: groupIdForAnalytics,
			select_id: selectId,
			link_id: linkId,
			...(journeyId ? { journey_id: journeyId } : {}),
		}),
		[groupIdForAnalytics, selectId, linkId, journeyId],
	);

	const isDisableAppleLogin = useMemo(() => {
		const localDisableApple = localStorage.getItem("disableApple");
		return disableApple || localDisableApple === "true";
	}, [disableApple]);

	const isUseCodeVerification = useMemo(() => {
		const localUseCodeVerification = localStorage.getItem(
			"useCodeVerification",
		);
		return (
			useCodeVerification ||
			localUseCodeVerification === "true" ||
			type === "native" ||
			type === "mobile" ||
			type === "csb" ||
			type === "extension" ||
			!groupId
		);
	}, [groupId, type, useCodeVerification]);

	useEffect(() => {
		setInIframe(window.top !== window.self);
	}, []);

	const handleOnFocusEmail = () => {
		// Renamed from `landing_page.auth_modal.email_input_focus`. The prefix
		// referred to the web client's old auth modal, which no longer handles
		// auth, and at 41 characters GA4 truncated the name on arrival to
		// `landing_page.auth_modal.email_input_focu`.
		GoogleAnalyticsService.logEvent(
			`email_input_focus`,
			{
				...signInIds,
			},
		);
	};

	const handleEmail = (event: any) => {
		const isValid = validateEmail(event.target.value);
		if (isValidEmail !== isValid) {
			setIsValidEmail(isValid);
		}
		setEmail(event.target.value);
	};

	const handleKeyDown = (event: any) => {
		if (event.key === "Enter" && isValidEmail) {
			handleEmail(event);
			handleSendEmail();
		} else {
			return;
		}
	};

	const handleVerificationCode = async (code: string) => {
		setLoading(true);
		setCodeError("");
		try {
			// Renamed from
			// `landing_page.auth_modal.email_code_verification_btn_click`, which
			// at 57 characters GA4 truncated on arrival to
			// `landing_page.auth_modal.email_code_verif`.
			await GoogleAnalyticsService.logEvent(
				"email_code_verification_btn_click",
				{
					...signInIds,
				},
			);
			const appUser = await fetchApi.verifyEmailCode(email, code);
			// 200 {"backoff": N} is the verify throttle, not a login. Treating it as
			// a user object previously advanced the flow with no session (phantom
			// age screen). Lock the Verify button for N seconds instead.
			if (appUser?.backoff != null) {
				// A dead end the person cannot proceed past, not an error to
				// swallow: this branch returned early with no row before.
				GoogleAnalyticsService.logEvent("onramp_dead_end", {
					reason: "rate_limited",
					stage: "code_verification",
					...signInIds,
				});
				setVerifyLockSeconds(Number(appUser.backoff) || 30);
				setVerifyLockNonce((n) => n + 1);
				setLoading(false);
				return;
			}
			// New user: identity verified, account deferred until the age check
			if (appUser?.signup_token) {
				await GoogleAnalyticsService.logEvent("email_verified", {
					...signInIds,
					is_recognized: 0,
				});
				markResolvedHere();
				await handlePendingSignup(appUser.signup_token, "email_code");
				setLoading(false);
				return;
			}
			if (!appUser?.id || !appUser?.jwtoken) {
				setCodeError(toI18N("lp_login_otp_error_generic"));
				setLoading(false);
				return;
			}
			setJwtToken(appUser.jwtoken);

			GoogleAnalyticsService.setUserId(appUser.id);
			await GoogleAnalyticsService.logEvent("email_verified", {
				...signInIds,
				is_recognized: appUser.new_user ? 0 : 1,
			});
			await GoogleAnalyticsService.logEvent(
				appUser.new_user ? "user_registration" : "user_login",
				{ method: "email_code", ...signInIds },
			);
			handleSignInSuccess(
				appUser.id,
				appUser.refresh_token,
				appUser.refresh_token_v2,
				appUser.user_phone?.phone_number,
				appUser.birthday,
				appUser.refresh_token_v2_expires_at,
			);
			setLoading(false);
		} catch (e: any) {
			setLoading(false);
			// Hard stop on the way in. The web client already emits
			// `onramp_dead_end` for its own failures (invalid invite, failed
			// join); reusing the name here puts both clients in one series, so
			// a dead end is countable wherever it happens. Params mirror the
			// web client's: `group_id` is the community UUID and `select_id`
			// the slug, both on every dead end. Not awaited: nothing downstream
			// depends on it and the user is already looking at an error. A 429
			// arrives here as a typed rate-limit error and is reported as such,
			// not as a wrong code.
			GoogleAnalyticsService.logEvent("onramp_dead_end", {
				reason: e?.code === "rate_limited" ? "rate_limited" : "code_invalid",
				stage: "code_verification",
				...signInIds,
			});
			// Inline under the code input (matches the age screen's error
			// pattern) rather than a transient toast the user can miss.
			setCodeError(authErrorMessage(e));
		}
	};

	const pollAdvancedToPendingRef = useRef(false);
	// The other guards are read before the request and written after it, so a
	// request slower than the interval would otherwise let a second tick sign in.
	const pollInFlightRef = useRef(false);
	// Going back abandons the flow, but cannot recall a request already sent.
	// The tick compares this on return and drops a result from a flow the user
	// has since left, which would otherwise push them back onto the age screen.
	const pollGenerationRef = useRef(0);

	const handleVerifyChallengeCompleted = async (challengeId: string) => {
		if (resolvedHereRef.current || pollInFlightRef.current) {
			return;
		}
		const generation = pollGenerationRef.current;
		pollInFlightRef.current = true;
		try {
			const appUser = await fetchApi.verifyChallengeId(challengeId);
			// Resolved or abandoned here while this request was in flight, so drop
			// the result rather than overwrite what the user just did.
			if (resolvedHereRef.current || generation !== pollGenerationRef.current) {
				return;
			}
			// New user: the link was confirmed on the other device but the account
			// is still pending age verification. Advance this tab once, then keep
			// polling, because the other device may finish the age check instead.
			if (appUser?.signup_token) {
				if (!pollAdvancedToPendingRef.current) {
					pollAdvancedToPendingRef.current = true;
					await handlePendingSignup(appUser.signup_token, "email_code");
					setLoading(false);
				}
				return;
			}
			markResolvedHere();
			setJwtToken(appUser.jwtoken);

			GoogleAnalyticsService.setUserId(appUser.id);
			await GoogleAnalyticsService.logEvent("email_verified", {
				...signInIds,
				is_recognized: appUser.new_user ? 0 : 1,
			});
			await GoogleAnalyticsService.logEvent(
				appUser.new_user ? "user_registration" : "user_login",
				{ method: "email_code", ...signInIds },
			);
			handleSignInSuccess(
				appUser.id,
				appUser.refresh_token,
				appUser.refresh_token_v2,
				appUser.user_phone?.phone_number,
				appUser.birthday,
				appUser.refresh_token_v2_expires_at,
			);
			setLoading(false);
		} catch (e: any) {
			// Non-2xx until the link is clicked, and again once the challenge
			// expires, so this is the waiting state rather than a failure.
		} finally {
			pollInFlightRef.current = false;
		}
	};

	// Called through a ref so the interval is created once yet always runs the
	// current closure, which carries the analytics fields.
	const verifyChallengeRef = useRef(handleVerifyChallengeCompleted);
	verifyChallengeRef.current = handleVerifyChallengeCompleted;

	// challengeId is minted on page load and is always set, so the gate is the
	// code-sent screen; polling on challengeId alone would hit every visitor.
	const challengeAwaitingConfirm =
		isCodeVerificationScreen && !challengeResolvedHere;
	useEffect(() => {
		if (!props.challengeId || !challengeAwaitingConfirm) {
			return;
		}
		const interval = setInterval(() => {
			verifyChallengeRef.current(props.challengeId);
		}, 2000);
		return () => clearInterval(interval);
	}, [props.challengeId, challengeAwaitingConfirm]);

	const handleProviderAuthRedirect = useCallback(
		async (code: string) => {
			setLoading(true);
			try {
				const userId = localStorage.getItem("temp-user-id");
				const provider = localStorage.getItem("provider");
				const linkId = localStorage.getItem("link-id");
				const siteDomain = window.location.origin;

				const appUser = await fetchApi.redirectProviderAuth(
					provider,
					code,
					linkId,
					userId,
					siteDomain,
				);
				// New user: identity verified, account deferred until the age check
				if (appUser?.signup_token) {
					await GoogleAnalyticsService.logEvent("oauth_verified", {
						group_id: groupIdForAnalytics,
						select_id: selectId,
						provider: provider,
						link_id: linkId,
						is_recognized: 0,
					});
					markResolvedHere();
					await handlePendingSignup(appUser.signup_token, provider || "oauth");
					return;
				}
				setJwtToken(appUser.jwtoken);

				GoogleAnalyticsService.setUserId(appUser.id);
				await GoogleAnalyticsService.logEvent("oauth_verified", {
					group_id: groupIdForAnalytics,
					select_id: selectId,
					provider: provider,
					link_id: linkId,
					is_recognized: appUser.new_user ? 0 : 1,
				});
				await GoogleAnalyticsService.logEvent(
					appUser.new_user ? "user_registration" : "user_login",
					{ method: provider, ...signInIds },
				);

				handleSignInSuccess(
					appUser.id,
					appUser.refresh_token,
					appUser.refresh_token_v2,
					appUser.user_phone?.phone_number,
					appUser.birthday,
					appUser.refresh_token_v2_expires_at,
				);
				setLoading(false);
			} catch (e) {
				// `needs_invitation` is kept apart from a generic provider
				// failure: it is a gating decision the product made, not a
				// technical fault, and conflating the two would make an
				// intentional block look like a bug in the OAuth path.
				GoogleAnalyticsService.logEvent("onramp_dead_end", {
					reason:
						e?.message === "New user needs invitation to join"
							? "needs_invitation"
							: "oauth_redirect_failed",
					stage: "oauth_redirect",
					group_id: groupIdForAnalytics,
					select_id: selectId,
					provider: localStorage.getItem("provider") || undefined,
					link_id: localStorage.getItem("link-id") || undefined,
				});
				if (e.message === "New user needs invitation to join") {
					toast.error(toI18N("lp_signup_need_community"));
				} else if (e && e.message) {
					toast.error("Something went wrong:" + e.message);
				} else {
					toast.error("Something went wrong");
				}
			} finally {
				setLoading(false);
			}
		},
		[setLoading, setJwtToken, selectId, groupIdForAnalytics, signInIds, handleSignInSuccess, handlePendingSignup, markResolvedHere],
	);

	const hasHandledProviderAuth = useRef(false);

	useEffect(() => {
		// isLinkRelay: location.replace doesn't cancel in-flight effects, so
		// without this skip the login exchange would race off (and burn the
		// relayed code) before the navigation away completes.
		if (isLinkRelay || !params.code || hasHandledProviderAuth.current) {
			return;
		}
		hasHandledProviderAuth.current = true;
		handleProviderAuthRedirect(params.code);
	}, [isLinkRelay, params.code, handleProviderAuthRedirect]);

	useEffect(() => {
		if (!params.challengeToken) {
			return;
		}

		const confirmChallenge = async (challengeToken: string) => {
			const appUser = await fetchApi.confirmChallengeToken(challengeToken);
			// New user: identity verified, account deferred until the age check
			if (appUser?.signup_token) {
				markResolvedHere();
				await handlePendingSignup(appUser.signup_token, "magic_link");
				setLoading(false);
				return;
			}
			setJwtToken(appUser.jwtoken);

			GoogleAnalyticsService.setUserId(appUser.id);
			await GoogleAnalyticsService.logEvent("email_verified", {
				...signInIds,
				is_recognized: appUser.new_user ? 0 : 1,
			});
			await GoogleAnalyticsService.logEvent(
				appUser.new_user ? "user_registration" : "user_login",
				{ method: "magic_link", ...signInIds },
			);
			handleSignInSuccess(
				appUser.id,
				appUser.refresh_token,
				appUser.refresh_token_v2,
				appUser.user_phone?.phone_number,
				appUser.birthday,
				appUser.refresh_token_v2_expires_at,
			);
			setLoading(false);
		};

		confirmChallenge(params.challengeToken)
			.then()
			.catch((e) => {
				// A dead or already-consumed magic link. This previously went to
				// console.error only, which is invisible in production — the
				// single most likely "expired link" case in the product, and the
				// one Q27 explicitly asks about.
				GoogleAnalyticsService.logEvent("onramp_dead_end", {
					reason: "magic_link_invalid",
					stage: "magic_link",
					...signInIds,
				});
				console.error(e);
			});
	}, [
		params.challengeToken,
		setJwtToken,
		selectId,
		groupIdForAnalytics,
		linkId,
		signInIds,
		setLoading,
		handleSignInSuccess,
		handlePendingSignup,
		markResolvedHere,
	]);

	const handleSendEmail = async () => {
		setLoading(true);
		stashBranding({ logo, title, coverImage, backgroundColor });
		if (isUseCodeVerification) {
			try {
				await GoogleAnalyticsService.logEvent(`email_verification_click`, {
					...signInIds,
				});
				const { token } = await fetchApi.requestEmailToken(
					email,
					linkId,
					props.challengeId,
				);
				if (token && isDev()) {
					setDebugEmailToken(token);
				}
				// `link_id`, as on every other event: this used to be camelCase
				// `linkId`, which GA4 stores as a different parameter.
				await GoogleAnalyticsService.logEvent(`email_verification_sent`, {
					link_id: linkId,
					group_id: groupIdForAnalytics,
					select_id: selectId,
					is_resend: false,
				});
				setIsCodeVerificationScreen(true);
			} catch (e: any) {
				// The code could not even be requested, so the user never
				// reaches the code screen. Distinct from `code_invalid`, which
				// is a wrong code typed on a screen they did reach.
				GoogleAnalyticsService.logEvent("onramp_dead_end", {
					reason: "code_request_failed",
					stage: "email_request",
					...signInIds,
				});
				isDev() && console.error(e);
				toast.error(authErrorMessage(e));
			} finally {
				setLoading(false);
			}
			return;
		}

		const emailTrimmed = email.trim();
		if (emailTrimmed) {
			try {
				await GoogleAnalyticsService.logEvent(`email_verification_click`, {
					...signInIds,
				});
				await fetchApi.invitationEmail(emailTrimmed, linkId, postSlug);
				toast.success(toI18N("lp_login_email_sent_toast"));
				await GoogleAnalyticsService.logEvent(`email_verification_sent`, {
					link_id: linkId,
					group_id: groupIdForAnalytics,
					select_id: selectId,
					post_slug: postSlug,
					is_resend: false,
				});
				setEmailSuccess(true);
			} catch (error: any) {
				GoogleAnalyticsService.logEvent("onramp_dead_end", {
					reason: "invitation_email_failed",
					stage: "email_request",
					...signInIds,
					post_slug: postSlug,
				});
				toast.error(error.message);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleResendEmailCode = async () => {
		const data = await fetchApi.requestEmailToken(email, linkId, props.challengeId);
		// Analytics only, success path only: a resend that failed still throws
		// to EmailScreen exactly as before. Resends were uncounted until now.
		GoogleAnalyticsService.logEvent(`email_verification_sent`, {
			link_id: linkId,
			group_id: groupIdForAnalytics,
			select_id: selectId,
			is_resend: true,
		});
		return data;
	};

	const handleStartProviderAuth = async (provider: string) => {
		setLoading(true);
		stashBranding({ logo, title, coverImage, backgroundColor });
		try {
			await GoogleAnalyticsService.logEvent(`oauth_started`, {
				provider,
				...signInIds,
			});
			const siteDomain = window.location.origin;
			const { access_uri: url } = await fetchApi.startProviderAuth(provider, siteDomain);

			localStorage.setItem("provider", provider);
			localStorage.setItem("type", type);
			localStorage.setItem("path", path);
			localStorage.setItem("disableApple", disableApple ? "true" : "false");
			localStorage.setItem(
				"useCodeVerification",
				useCodeVerification ? "true" : "false",
			);

			if (linkId) {
				localStorage.setItem("link-id", linkId);
			}
			const redirectUri = query.get("redirectUri");
			if (redirectUri) {
				localStorage.setItem("redirectUri", redirectUri);
			}

			// Encode auth state in the OAuth state parameter so it survives
			// ASWebAuthenticationSession where localStorage may not persist.
			// selectId / groupId ride along for analytics only, so the events
			// on the return leg can name the community.
			const oauthUrl = new URL(url);
			const originalState = oauthUrl.searchParams.get("state") || "";
			const stateData = btoa(
				JSON.stringify({
					provider,
					type,
					redirectUri,
					linkId,
					selectId,
					groupId: groupIdForAnalytics,
					journeyId,
					oauthRequest: props.oauthRequest,
				}),
			);
			oauthUrl.searchParams.set("state", `${originalState}.${stateData}`);
			window.location.replace(oauthUrl.toString());
		} catch (e: any) {
			// Failed before the user ever reached the provider — pairs with the
			// existing `oauth_started`, so the gap between the two is now
			// measurable rather than silent.
			GoogleAnalyticsService.logEvent("onramp_dead_end", {
				reason: "oauth_start_failed",
				stage: "oauth_start",
				group_id: groupIdForAnalytics,
				select_id: selectId,
				provider,
				link_id: linkId,
			});
			toast.error(e.message);
		} finally {
			setLoading(false);
		}
	};

	const handleVerificationComplete = useCallback(
		async (userId: string, refreshToken: string, refreshTokenV2?: string, birthday?: string) => {
			markResolvedHere();
			// Check if user already has birthday set and is over 16
			if (birthday && validateAge(birthday)) {
				// User already has birthday and is over 16, skip birthday verification
				if (handleBirthdayVerifiedRef.current) {
					await handleBirthdayVerifiedRef.current(userId, refreshToken, refreshTokenV2);
				}
				return;
			}

			// After phone verification is complete (for China users without phone number), proceed to birthday verification
			// This maintains the unified flow where all signup methods require birthday verification as the final step
			setUserIdForBirthdayVerification(userId);
			setRefreshTokenForBirthdayVerification(refreshToken);
			setRefreshTokenV2ForBirthdayVerification(refreshTokenV2);
			setBirthdayVerificationRequired(true);
		},
		[
			validateAge,
			setUserIdForBirthdayVerification,
			setRefreshTokenForBirthdayVerification,
			setBirthdayVerificationRequired,
			markResolvedHere,
		],
	);

	const handleBack = () => {
		// Starting over needs a clean slate, or the pending path stays latched
		// and a second attempt never advances.
		pollGenerationRef.current += 1;
		pollAdvancedToPendingRef.current = false;
		releaseResolvedHere();
		setEmailSuccess(false);
		setCodeError("");
		setIsCodeVerificationScreen(false);
		setPhoneVerificationRequired(false); // Also go back from phone verification if needed
		setBirthdayVerificationRequired(false); // Also go back from birthday verification if needed
		setSignupToken(""); // Abandon any pending signup so a later login can't pick it up
		setSignupMethod("");
	};

	// Define handleBirthdayVerified after all dependencies are declared
	const handleBirthdayVerified = useCallback(
		async (userId: string, refreshToken: string, refreshTokenV2?: string, expiresAtOverride?: string) => {
			// expiresAtOverride: pending signups get their tokens in the same tick
			// as this call, so the expiry can't be read from state yet.
			const expiresAt = expiresAtOverride ?? refreshTokenExpiresAt;
			const tokenForWeb = refreshTokenV2 || refreshToken;
			const sessionEstablished = async () => {
				try {
					await establishSession(tokenForWeb);
					return true;
				} catch {
					GoogleAnalyticsService.logEvent("onramp_dead_end", {
						reason: "session_establish_failed",
						stage: "session",
						...signInIds,
					});
					toast.error(toI18N("lp_login_otp_error_generic"));
					return false;
				}
			};
			// Before emitLoginSuccess: in a popup-based OIDC login the opener is
			// the third-party site, which must never receive the refresh token.
			if (props.onAuthenticated) {
				if (!(await sessionEstablished())) return;
				AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
				props.onAuthenticated();
				return;
			}
			// Chrome extensions need "*" — HomeURL origin cannot deliver to chrome-extension://
			await emitLoginSuccess(
				userId,
				tokenForWeb,
				type === "extension" ? "*" : undefined,
			);

			// Read redirectUri before clearing localStorage (needed for mobile type after OAuth redirect)
			const redirectUri =
				localStorage.getItem("redirectUri") || query.get("redirectUri");

			// Clear only auth-related localStorage keys instead of clearing everything
			AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

      const isNewUser = birthdayVerificationRequired || phoneVerificationRequired;
			if (type === "native") {
				/* tslint:disable-next-line */
				(window as any).ReactNativeWebView.postMessage(
					JSON.stringify({ userId, refreshToken, isNewUser }),
				);
				return;
			}
			if (type === "extension") {
				// Browser extension host (iframe parent or window.opener).
				// Post with "*" so chrome-extension:// pages can receive the callback.
				// Do not redirect.
				const callbackPayload = {
					type: "login-callback",
					message: JSON.stringify({
						userId,
						refreshToken: tokenForWeb,
						expiresAt: expiresAt,
						isNewUser: isNewUser,
					}),
				};
				if (window.opener) {
					window.opener.postMessage(callbackPayload, "*");
					window.close();
					return;
				}
				if (window.parent && window.parent !== window) {
					window.parent.postMessage(callbackPayload, "*");
					return;
				}
				return;
			}
			if (type === "mobile") {
				if (redirectUri && isValidMobileRedirectUri(redirectUri)) {
					const callbackUrl = new URL(redirectUri);
					callbackUrl.searchParams.set("refreshToken", refreshToken);
					callbackUrl.searchParams.set("userId", userId);
					callbackUrl.searchParams.set("isNewUser", isNewUser.toString());
					window.location.replace(callbackUrl.toString());
					return;
				}
			}
			if (!(await sessionEstablished())) return;
			if (type === "iframe") {
				window.parent.postMessage(
					{
						type: "login-callback",
						message: JSON.stringify({ userId, isNewUser }),
					},
					getTargetOrigin(),
				);
				if (!params.code) {
					return;
				}
			}
			if (type === "popup") {
				if (window.opener) {
					window.opener.postMessage(
						{
							type: "login-callback",
							message: JSON.stringify({ userId, isNewUser }),
						},
						getTargetOrigin(),
					);
					window.close();
					return;
				}
				// window.opener unavailable (mobile browsers due to ITP / cross-origin).
				// Fall through to standard web redirect below.
			}

			// Validate redirect URL to prevent open redirect attacks
			let redirectUrl = path || HomeURL;
			if (!isValidRedirectUrl(redirectUrl)) {
				redirectUrl = HomeURL; // Fall back to safe default
			}

			if (linkId && !redirectUrl.includes("linkId")) {
				redirectUrl = `${redirectUrl}?invitationLinkId=${encodeURIComponent(linkId)}`;
			}

			if (isNewUser) {
				const separator = redirectUrl.includes("?") ? "&" : "?";
				redirectUrl = `${redirectUrl}${separator}isNewUser=true`;
			}

			window.location.replace(redirectUrl);
			setSuccessSignIn(true);
		},
		[type, path, params.code, linkId, refreshTokenExpiresAt, birthdayVerificationRequired, phoneVerificationRequired, query, props, signInIds],
	);

	useEffect(() => {
		handleBirthdayVerifiedRef.current = handleBirthdayVerified;
	}, [handleBirthdayVerified]);

	// Pending signup completed: /complete-signup created the account and
	// returned the logged-in payload. Register the session the same way the
	// existing-account path does after its birthday update.
	const handleSignupCompleted = useCallback(
		async (appUser: any) => {
			markResolvedHere();
			setJwtToken(appUser.jwtoken);
			setRefreshTokenExpiresAt(appUser.refresh_token_v2_expires_at);
			GoogleAnalyticsService.setUserId(appUser.id);
			await GoogleAnalyticsService.logEvent("user_registration", {
				method: signupMethod || "email_code",
				...signInIds,
			});
			await handleBirthdayVerified(
				appUser.id,
				appUser.refresh_token,
				appUser.refresh_token_v2,
				appUser.refresh_token_v2_expires_at,
			);
		},
		[signupMethod, signInIds, handleBirthdayVerified, markResolvedHere],
	);

	if (successSignIn) {
		return <></>;
	}

	// Relay in progress — the browser is navigating to the requesting page;
	// show only a loader instead of flashing the login UI. (Safe early
	// return: every hook above has already run.)
	if (isLinkRelay) {
		return (
			<div className="fixed inset-0">
				<Loader />
			</div>
		);
	}

	if (birthdayVerificationRequired) {
		return (
			<BirthdayVerification
				journeyId={journeyId}
				userId={userIdForBirthdayVerification}
				jwtToken={jwtToken}
				email={email}
				refreshToken={refreshTokenForBirthdayVerification}
				refreshTokenV2={refreshTokenV2ForBirthdayVerification}
				phoneNumber={phoneNumberForBirthdayVerification}
				coverImage={coverImage}
				backgroundColor={backgroundColor}
				logo={logo}
				title={title}
				handleBack={handleBack}
				handleBirthdayVerified={handleBirthdayVerified}
				loading={loading}
				signupToken={signupToken}
				handleSignupCompleted={handleSignupCompleted}
				onSignupSubmitted={markResolvedHere}
				onSignupSubmitFailed={releaseResolvedHere}
				selectId={selectId}
				groupId={groupIdForAnalytics}
				linkId={linkId}
			/>
		);
	}

	if (phoneVerificationRequired) {
		return (
			<PhoneVerification
				userId={userIdForPhoneVerification}
				email={email}
				jwtToken={jwtToken}
				coverImage={coverImage}
				backgroundColor={backgroundColor}
				logo={logo}
				title={title}
				handleBack={handleBack}
				handlePhoneVerificationComplete={handleVerificationComplete}
				checkingLocation={isCheckingLocation}
				signupToken={signupToken}
				handlePendingPhoneVerified={handlePendingPhoneVerified}
			/>
		);
	}

	if (emailSuccess || isCodeVerificationScreen) {
		return (
			<EmailScreen
				handleBack={handleBack}
				useCodeVerification={isUseCodeVerification}
				debugEmailToken={debugEmailToken}
				email={email}
				loading={loading}
				coverImage={coverImage}
				backgroundColor={backgroundColor}
				logo={logo}
				title={title}
				codeError={codeError}
				onClearCodeError={() => setCodeError("")}
				handleVerificationCode={handleVerificationCode}
				verifyLockSeconds={verifyLockSeconds}
				verifyLockNonce={verifyLockNonce}
				onResendCode={handleResendEmailCode}
			/>
		);
	}

	const identityProviders = (
		isDisableAppleLogin ? ["google"] : ["google", "apple"]
	).map((provider) => {
		const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);

		return (
			<button
				key={provider}
				onClick={() => handleStartProviderAuth(provider)}
				className="w-full mb-3 flex items-center justify-center bg-neutral-1 text-neutral-2 p-3 border border-neutral-11 hover:text-neutral-1 hover:bg-primary-2"
				style={{
					borderRadius: "24px",
					fontSize: "16px",
					fontWeight: "700",
					lineHeight: "18px",
					letterSpacing: "0em",
					textAlign: "center",
				}}
			>
				<img
					src={`/${provider}.svg`}
					alt=""
					aria-hidden="true"
					className="mr-2 h-4 w-4"
				/>
				{!groupId && !linkId
					? formatI18nKeys("lp_login_signup_login_with", providerName)
					: formatI18nKeys("lp_login_signup_continue_with", providerName)}
			</button>
		);
	});

	return (
		<Wrapper
			InIframe={inIframe}
			isMobile={size.width < 481}
			coverImage={coverImage}
			backgroundColor={backgroundColor}
		>
			<div className="p-8" aria-busy={loading}>
				<div className="flex flex-col items-center">
					<AvatarMedia
						src={logo ? logo : "/logo.svg"}
						alt={title ? `${title} logo` : "Select logo"}
						className={`mb-3 w-20 h-20 ${
							logo && logo !== "/logo.svg"
								? "rounded-full object-cover border-2 border-primary-2"
								: ""
						}`}
					/>
					<h1
						className="mb-5 text-[24px] pb-12 font-bold text-center"
						style={{
							fontSize: "24px",
							fontWeight: "700",
							lineHeight: "29px",
							letterSpacing: "0em",
						}}
					>
						{title || toI18N("lp_login_signup_fallback_title")}{" "}
					</h1>
					{identityProviders}
					<div
						className="text-center mb-3"
						style={{
							fontSize: "16px",
							fontWeight: "700",
							lineHeight: "19px",
							letterSpacing: "0em",
							textAlign: "left",
						}}
					>
						{toI18N("lp_login_signup_or")}
					</div>
					<label htmlFor="email-input" className="sr-only">
						{toI18N("lp_login_email_placeholder")}
					</label>
					<input
						id="email-input"
						type="email"
						placeholder={toI18N("lp_login_email_placeholder")}
						className="mb-3 w-full border border-neutral-11 p-3 rounded bg-primary-4"
						style={{ borderRadius: "8px" }}
						onKeyDown={handleKeyDown}
						onChange={handleEmail}
						onFocus={handleOnFocusEmail}
						value={email}
					/>
					<button
						onClick={handleSendEmail}
						disabled={!isValidEmail}
						className={`w-full mb-3 p-3 border border-neutral-11 text-neutral-1 ${
							isValidEmail
								? "bg-primary-1 hover:bg-primary-2"
								: "bg-neutral-7 cursor-not-allowed"
						}`}
						style={{
							borderRadius: "24px",
							fontSize: "16px",
							fontWeight: "700",
							lineHeight: "18px",
							letterSpacing: "0em",
							textAlign: "center",
						}}
					>
						{toI18N("lp_login_signup_email_continue")}
					</button>
				</div>

				{/* <p>{`linkId: ${linkId}`}</p>
        <p>{`type: ${type}`}</p>
        <p>{`path: ${path}`}</p>
        <p>{`Env: ${Env}`}</p>
        <p>{`HomeURL: ${HomeURL}`}</p>
        <p>{`equal: ${Env === "development"}`}</p>
        <p>{`isDev: ${isDev()}`}</p> */}
				<div className="mt-5 text-xs text-center text-gray-500">
					<p className="mb-2">{toI18N("lp_login_signup_privacy")}</p>
					<p>
						By continuing, you agree to our{" "}
						<a
							href="https://the.select/terms"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary-1 hover:text-primary-2 underline"
						>
							{toI18N("general_terms")}
						</a>{" "}
						and{" "}
						<a
							href="https://the.select/privacy"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary-1 hover:text-primary-2 underline"
						>
							{toI18N("general_privacy")}
						</a>
						.
					</p>
				</div>

				<Toaster position="bottom-center" />
				{loading && (
					<div className="fixed inset-0 z-50">
						<div className="absolute inset-0 bg-primary-4 opacity-50" />
						<div className="absolute inset-0">
							<Loader />
						</div>
					</div>
				)}
			</div>
		</Wrapper>
	);
}

export default Signup;
